import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { ProgressBar } from "@/components/ProgressBar";
import { Building2, Flame } from "lucide-react";
import { Stat, Empty, Skeleton } from "./shared";

function StreakBoard({ students }) {
  const maxStreak = Math.max(1, ...students.map((s) => s.current_streak));
  return (
    <div className="rounded-3xl border-2 border-ink bg-white shadow-pop-sm p-6">
      <h3 className="font-extrabold text-lg mb-4 flex items-center gap-2"><Flame size={18} style={{ color: "#FF5722" }} /> Streak leaderboard</h3>
      <div className="space-y-3">
        {students.slice(0, 10).map((s) => (
          <div key={s.id} data-testid={`leader-row-${s.id}`}>
            <div className="flex justify-between text-sm font-bold mb-1">
              <span className="truncate">{s.name} {s.organization_name ? <span className="text-muted-foreground font-normal">· {s.organization_name}</span> : null}</span>
              <span className="flex items-center gap-1 shrink-0"><Flame size={13} style={{ color: "#FF5722" }} />{s.current_streak}d · {s.completion_pct}%</span>
            </div>
            <div className="h-3 rounded-full border-2 border-ink bg-white overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(s.current_streak / maxStreak) * 100}%`, background: "#FF5722" }} />
            </div>
          </div>
        ))}
        {students.length === 0 && <Empty text="No students yet." />}
      </div>
    </div>
  );
}

function OrgBreakdown({ rows, scope }) {
  return (
    <div className="rounded-3xl border-2 border-ink bg-white shadow-pop-sm p-6">
      <h3 className="font-extrabold text-lg mb-4 flex items-center gap-2"><Building2 size={18} /> {scope === "all" ? "By organization" : "Completion"}</h3>
      <div className="space-y-3">
        {rows.map((o) => (
          <div key={o.organization_name} className="rounded-2xl border-2 border-ink p-3" style={{ background: "#FFFDF5" }} data-testid={`org-stat-${o.organization_name}`}>
            <div className="flex justify-between font-bold text-sm">
              <span className="truncate">{o.organization_name}</span>
              <span>{o.avg_completion}%</span>
            </div>
            <div className="text-xs text-muted-foreground">{o.students} students · {o.active_7d} active this week</div>
            <ProgressBar value={o.avg_completion} color="#31B8FF" height={7} />
          </div>
        ))}
        {rows.length === 0 && <Empty text="No data yet." />}
      </div>
    </div>
  );
}

export function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet("/manage/analytics/detail").then(setData).catch((e) => toast.error(e.message)); }, []);
  if (!data) return <Skeleton />;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Students" value={data.totals.students} color="#FF5722" />
        <Stat label="Active (7d)" value={data.totals.active_7d} color="#31B8FF" />
        <Stat label="Avg completion" value={`${data.totals.avg_completion}%`} color="#B8FF3B" />
        <Stat label="Days completed" value={data.totals.total_days_completed} color="#FFD93D" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <StreakBoard students={data.students} />
        <OrgBreakdown rows={data.org_breakdown} scope={data.scope} />
      </div>
    </div>
  );
}
